import { useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { insightArticles } from '@/mocks/insights';
import useLightReveal from '@/hooks/useLightReveal';

export default function InsightsPage() {
  const ref = useRef<HTMLElement>(null);
  useLightReveal(ref, 'insights');
  const [feature, ...rest] = insightArticles;

  return (
    <>
      <title>Insights | AUREN Management Consulting — Strategy &amp; Leadership</title>
      <meta name="description" content="AUREN perspectives on strategy, operating models, leadership alignment and focus — short editorial notes on how organizations make clearer decisions." />
      <Navbar />
      <main ref={ref} className="bg-background-50 pt-14">
        <section className="wrap pt-20 md:pt-32 pb-12 md:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-8 items-end">
            <div className="lg:col-span-8" data-fade>
              <span className="label-xs text-foreground-600">AUREN / Perspectives</span>
              <h1 className="font-heading font-light uppercase leading-[0.92] tracking-[0.02em] text-foreground-950 mt-6 text-[clamp(44px,7vw,100px)]">Insights.</h1>
            </div>
            <p className="lg:col-span-4 lg:text-right font-heading font-light uppercase tracking-[0.04em] leading-[1.35] text-[17px] md:text-[20px] text-foreground-950 max-w-[360px] lg:justify-self-end lg:pb-3" data-fade>
              Perspectives on strategy, leadership and change.
            </p>
          </div>
        </section>

        <section className="wrap pb-24 md:pb-32" aria-label="Articles">
          <div className="flex items-center justify-between border-t border-foreground-950/15 pt-4 mb-10 md:mb-14">
            <span className="label-xs text-foreground-600">Featured</span>
            <span className="label-xs text-foreground-600">01&mdash;{String(insightArticles.length).padStart(2, '0')}</span>
          </div>

          <article data-fade>
            <Link to={`/insights/${feature.slug}`} className="group grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-7 items-center cursor-pointer focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent-500 focus-visible:outline-offset-4">
              <div className="lg:col-span-7 w-full aspect-[16/10] overflow-hidden bg-background-200">
                <img src={feature.cover} alt={`${feature.title} — AUREN insight`} title={feature.title} className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
              </div>
              <div className="lg:col-span-4 lg:col-start-9">
                <span className="label-xs text-foreground-600">{feature.category} <span className="mx-2">/</span> {feature.date}</span>
                <h2 className="mt-5 font-heading font-light uppercase tracking-[0.02em] leading-[1.05] text-foreground-950 group-hover:text-primary-500 transition-colors text-[clamp(28px,3.2vw,44px)]">{feature.title}</h2>
                <p className="mt-5 text-[15px] leading-[1.65] text-foreground-700 max-w-[380px]">{feature.summary}</p>
                <span className="circle-arrow inv-dark text-foreground-950 mt-8"><i className="ri-arrow-right-line" /></span>
              </div>
            </Link>
          </article>

          <div className="mt-20 md:mt-28 border-t border-foreground-950/15">
            {rest.map((a) => (
              <article key={a.slug} className="border-b border-foreground-950/15" data-fade>
                <Link to={`/insights/${a.slug}`} className="group grid grid-cols-[96px_1fr] sm:grid-cols-[160px_1fr_auto] lg:grid-cols-12 gap-x-5 lg:gap-x-8 items-center py-6 md:py-8 cursor-pointer focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent-500">
                  <div className="lg:col-span-2 w-full aspect-[4/3] overflow-hidden bg-background-200">
                    <img src={a.cover} alt={`${a.title} — AUREN insight`} title={a.title} loading="lazy" className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                  </div>
                  <div className="lg:col-span-6 lg:col-start-4 min-w-0">
                    <span className="label-xs text-foreground-600">{a.category} <span className="mx-2">/</span> {a.date}</span>
                    <h3 className="mt-2.5 font-heading font-light uppercase tracking-[0.04em] leading-[1.2] text-[17px] md:text-[23px] text-foreground-950 group-hover:text-primary-500 transition-colors">{a.title}</h3>
                  </div>
                  <p className="hidden lg:block lg:col-span-2 text-sm leading-[1.6] text-foreground-700">{a.summary}</p>
                  <span className="hidden sm:flex lg:col-span-1 justify-self-end"><span className="circle-arrow inv-dark text-foreground-950"><i className="ri-arrow-right-line" /></span></span>
                </Link>
              </article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}